import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../../shared/api/client';
import { useCurrentUser } from '../auth/useCurrentUser';

/**
 * A warehouse this user may work in.
 *
 * Derived on the server from their group grants — a grant carries the warehouse it applies in —
 * so this is not the tenant's warehouse list filtered on the client. It is the answer to "where
 * may you work", and an empty array is a real answer: nobody has set this account up yet.
 */
export interface ReachableWarehouse {
  warehouseId: string;
  warehouseCode: string;
  displayName: string;
}

export const REACHABLE_WAREHOUSES_KEY = ['auth', 'me', 'warehouses'] as const;

/**
 * Where the current user may work.
 *
 * `staleTime: 0`, for the same reason the permissions query has it: an administrator granting
 * somebody a warehouse has to take effect on their next call, not their next sign-in.
 *
 * Distinct from `useWarehouses`, which lists the tenant's warehouses for maintenance screens and
 * needs the inactive ones. Reachability is not a filter over that list — a tenant administrator
 * reaches every warehouse without holding a single grant.
 */
export function useReachableWarehouses() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: REACHABLE_WAREHOUSES_KEY,
    // Gated on a signed-in user so the anonymous screens do not fire a request that can only
    // come back 401.
    enabled: Boolean(user),
    staleTime: 0,
    retry: false,
    queryFn: async ({ signal }) => {
      const { data } = await apiRequest<{ warehouses: ReachableWarehouse[] }>(
        '/v1/auth/me/warehouses',
        { method: 'GET', signal, credentials: 'include' },
      );
      return data.warehouses;
    },
  });
}
